import { call, put } from 'redux-saga/effects'
import { delay, END } from 'redux-saga'

import {
  companyNamesOf,
  errorCodeOf,
  ERROR_CODE_ACTUALS_CALCULATION_IN_PROGRESS,
} from 'api/api-utils'
import { tryParseErrorJson } from 'utils/parseError'

// The API waits for an in-flight actuals calculation itself and only answers
// ACTUALS_CALCULATION_IN_PROGRESS_CODE once it has stopped holding a request thread on it, so a
// retry is not a poll of something that just failed - it is picking the wait back up. The delay
// only keeps a low server-side wait budget from turning into a tight loop.
export const CALCULATION_RETRY_DELAY_MS = 2000

// The API's own liveness marker expires within a couple of minutes of a calculation dying, so this
// is not what recovers from a stuck calculation. It is the backstop for the case where the marker
// itself gets stuck: a real error is better than a spinner that never resolves.
export const CALCULATION_MAX_TOTAL_WAIT_MS = 10 * 60 * 1000

/**
 * Inspects an `event:error` frame from a report-data stream. If it is the "an actuals calculation
 * is holding this company" refusal, records `{ error, companies }` onto `outcome.calculationInProgress`
 * (the caller's cue to retry), emits END to close the channel, and returns `{ refused: true, parsedError }`.
 * Otherwise it does nothing to `outcome` or the channel and returns `{ refused: false, parsedError }`,
 * leaving the generic error to the caller - parsed only once either way.
 */
export const recordCalculationRefusal = (event, outcome, emit) => {
  const parsedError = tryParseErrorJson(event.data)

  if (errorCodeOf(parsedError) !== ERROR_CODE_ACTUALS_CALCULATION_IN_PROGRESS) {
    return { refused: false, parsedError }
  }

  // Not a failure: the figures of one of this report's companies are being rewritten, and half of
  // a rewrite is not something worth showing. Kept out of the error state so the view stays
  // loading while the read is retried.
  outcome.calculationInProgress = {
    error: parsedError,
    companies: companyNamesOf(parsedError),
  }
  emit(END)
  return { refused: true, parsedError }
}

/**
 * Reads a report-shaped stream, asking again for as long as the API keeps refusing with "an
 * actuals calculation is holding this company", and gives up with the API's own error once
 * `CALCULATION_MAX_TOTAL_WAIT_MS` has been spent waiting.
 *
 * `readOnce` is called as `call(readOnce, outcome)` once per attempt. It must report back by
 * writing `{ error, companies }` onto `outcome.calculationInProgress` when the API refused, and by
 * leaving `outcome` alone when the read finished - with data, or with an error it already
 * dispatched itself. It cannot report back through a return value: `readOnce` normally closes its
 * channel with END once done, and redux-saga 0.16 answers END by *terminating* the generator
 * blocked on `take(channel)` (proc.js calls `iterator.return()`) rather than resuming it - nothing
 * after that `take` runs, and `call()` of that generator always resolves to undefined.
 *
 * `waiting(companies)` and `giveUp(frame)` are plain functions returning actions; this loop is the
 * only thing that dispatches them.
 */
export function* readUntilCalculationFinished(readOnce, { waiting, giveUp }) {
  const startedAt = Date.now()
  while (true) {
    const outcome = {}
    yield call(readOnce, outcome)

    const { calculationInProgress } = outcome
    if (!calculationInProgress) return

    // Waited long enough that the report is not coming: report the API's own message rather than
    // leaving the view loading forever.
    if (Date.now() - startedAt >= CALCULATION_MAX_TOTAL_WAIT_MS) {
      yield put(giveUp(calculationInProgress.error))
      return
    }

    yield put(waiting(calculationInProgress.companies))
    // redux-saga 0.16 has no delay effect - delay is a promise helper, so it goes through call.
    yield call(delay, CALCULATION_RETRY_DELAY_MS)
  }
}

// The message key RealBalanceCalculationGate.SendProgress uses while it waits inside a single
// request (api/Finazilla/src/Finazilla.Core/Managers/RealBalanceCalculationGate.cs). Reused here so
// the wait between two requests reads as the same sentence as the wait inside one of them.
const WAITING_FOR_CALCULATION_MESSAGE_KEY =
  'progress.waitingForActualsCalculation'

/**
 * Synthesizes the same JSON a `progress` SSE frame carries for `WAITING_FOR_CALCULATION_MESSAGE_KEY`
 * - see `ServerSentEventsManager.SendProgress(string messageKey, Dictionary<string,string> parameters,
 * string fallbackText)`, which does `JsonSerializer.Serialize(new ProgressMessageDto(messageKey,
 * parameters) { FallbackText = fallbackText })` with the runtime's default (PascalCase) naming, i.e.
 * `{ MessageKey, Params, FallbackText }`. Mirrored exactly so a reducer can drop this string straight
 * into the same `progress` field the API's own stream fills, and `parseProgressMessage` renders it
 * with no container changes.
 */
export const waitingForCalculationProgress = (companies) => {
  const company = (companies || []).join(', ')
  return JSON.stringify({
    MessageKey: WAITING_FOR_CALCULATION_MESSAGE_KEY,
    Params: { company },
    FallbackText: `Waiting for actuals calculation to finish: ${company}`,
  })
}
